import { useEffect, useState } from 'react';

interface WebSocketMessage {
  type: string;
  [key: string]: any;
}

class WebSocketService {
  private socket: WebSocket | null = null;
  private messageHandlers: { [key: string]: ((data: any) => void)[] } = {};
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 2000;
  private isConnecting = false;

  constructor(private url: string) {}

  connect(): Promise<void> {
    if (this.socket?.readyState === WebSocket.OPEN || this.isConnecting) {
      return Promise.resolve();
    }

    this.isConnecting = true;
    
    return new Promise((resolve, reject) => {
      try {
        this.socket = new WebSocket(this.url);
        
        this.socket.onopen = () => {
          console.log('WebSocket connection established');
          this.reconnectAttempts = 0;
          this.isConnecting = false;
          resolve();
        };
        
        this.socket.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data) as WebSocketMessage;
            this.handleMessage(message);
          } catch (error) {
            console.error('Error parsing WebSocket message', error);
          }
        };
        
        this.socket.onclose = (event) => {
          this.isConnecting = false;
          if (!event.wasClean) {
            console.warn(`WebSocket connection closed unexpectedly: ${event.code}`);
            this.attemptReconnect();
          } else {
            console.log('WebSocket connection closed cleanly');
          }
        };
        
        this.socket.onerror = (error) => {
          console.error('WebSocket error', error);
          this.isConnecting = false;
          reject(error);
        };
      } catch (error) {
        this.isConnecting = false;
        reject(error);
      }
    });
  }

  private attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Maximum reconnection attempts reached');
      return;
    }
    
    this.reconnectAttempts++;
    console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
    
    setTimeout(() => {
      this.connect().catch(() => {
        // Error handling is done in connect()
      });
    }, this.reconnectDelay * this.reconnectAttempts);
  }

  subscribe(messageType: string, callback: (data: any) => void) {
    if (!this.messageHandlers[messageType]) {
      this.messageHandlers[messageType] = [];
    }
    this.messageHandlers[messageType].push(callback);
    
    return () => this.unsubscribe(messageType, callback);
  }

  unsubscribe(messageType: string, callback: (data: any) => void) {
    if (this.messageHandlers[messageType]) {
      this.messageHandlers[messageType] = this.messageHandlers[messageType].filter(
        (handler) => handler !== callback
      );
    }
  }

  private handleMessage(message: WebSocketMessage) {
    const { type, ...data } = message;
    
    if (this.messageHandlers[type]) {
      this.messageHandlers[type].forEach((handler) => handler(data));
    }
    
    // Special handler for all messages
    if (this.messageHandlers['*']) {
      this.messageHandlers['*'].forEach((handler) => handler(message));
    }
  }

  send(message: WebSocketMessage): boolean {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.error('Cannot send message: WebSocket is not connected');
      return false;
    }
    
    try {
      this.socket.send(JSON.stringify(message));
      return true;
    } catch (error) {
      console.error('Error sending WebSocket message', error);
      return false;
    }
  }

  disconnect() {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    this.messageHandlers = {};
  }
}

// Create singleton instance - use secure WebSockets for production
const WS_URL = process.env.REACT_APP_WS_URL || 'wss://ws.yourbetai.com';
export const webSocketService = new WebSocketService(WS_URL);

// React hook for using WebSocket in components
export function useWebSocket<T>(messageType: string, initialData?: T) {
  const [data, setData] = useState<T | undefined>(initialData);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    // Connect to WebSocket
    webSocketService.connect()
      .then(() => setIsConnected(true))
      .catch((error) => console.error('WebSocket connection error', error));
    
    // Subscribe to message type
    const unsubscribe = webSocketService.subscribe(messageType, (newData) => {
      setData(newData as T);
    });
    
    // Cleanup on unmount
    return () => {
      unsubscribe();
    };
  }, [messageType]);

  return { data, isConnected };
}
